import React, { useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    VStack,
    useToast,
    useDisclosure,
} from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Navbar from '../../../components/Navbar';
import { useWeighbridgeReport } from '../hooks/useWeighbridgeReport';
import { FilterWeighbridge } from '../components/FilterWeighbridge';
import { WeighbridgeTable } from '../components/WeighbridgeTable';
import { ColumnVisibilityModal } from '../components/ColumnVisibilityModal';
import { WeighbridgeFilters } from '../types';
import { useRbac } from '../../../rbac/RbacContext';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 5 * 60 * 1000, // 5 menit
        },
    },
});

const WeighbridgeContent: React.FC = () => {
    const { isAdmin } = useRbac();
    const {
        records,
        pagination,
        loading,
        isExporting,
        error,
        filters,
        visibleColumns,
        setVisibleColumns,
        fetchData,
        changePage,
        changePerPage,
        exportExcel,
    } = useWeighbridgeReport();

    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        if (error) {
            toast({
                title: 'Gagal Memuat Data',
                description: error,
                status: 'error',
                duration: 6000,
                isClosable: true,
                position: 'top-right',
            });
        }
    }, [error, toast]);

    const handleApplyFilters = (newFilters: WeighbridgeFilters) => {
        fetchData(newFilters);
    };

    return (
        <Box minH="100vh" bg="gray.50">
            <Navbar />

            <Box flex="1" p={{ base: 4, md: 6 }}>
                <VStack spacing={5} align="stretch">
                    <VStack align="start" spacing={1}>
                        <Heading size="lg" color="gray.700">
                            Laporan Timbang
                        </Heading>
                        <Text color="gray.600" fontSize="sm">
                            Dashboard Laporan Timbang
                        </Text>
                    </VStack>

                    <FilterWeighbridge
                        filters={filters}
                        onApply={handleApplyFilters}
                        onExportExcel={exportExcel}
                        onToggleColumns={onOpen}
                        isAdmin={isAdmin}
                        isLoading={loading}
                        isExporting={isExporting}
                        hasData={pagination.total_records > 0}
                    />

                    <WeighbridgeTable
                        records={records}
                        visibleColumns={visibleColumns}
                        pagination={pagination}
                        onChangePage={changePage}
                        onChangePerPage={changePerPage}
                        loading={loading}
                        error={error}
                    />
                </VStack>
            </Box>

            <ColumnVisibilityModal
                isOpen={isOpen}
                onClose={onClose}
                visibleColumns={visibleColumns}
                onChangeColumns={setVisibleColumns}
            />
        </Box>
    );
};

export const WeighbridgePage: React.FC = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <WeighbridgeContent />
        </QueryClientProvider>
    );
};

